'use client';

import Cookies from 'js-cookie';
import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Box, TextField, Alert, Typography, Stack, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, MailOutline, LockOutlined } from '@mui/icons-material';
import LoginLayout, { AuthSubmitButton, authInputSx } from '@/components/LoginLayout';
import { post } from '@/lib/fetch';

const clearAllCookies = () => {
  const allCookies = Cookies.get();
  Object.keys(allCookies).forEach((cookie) => Cookies.remove(cookie));
};

function SearchParamAlerts() {
  const searchParams = useSearchParams();
  const errorParam = searchParams.get('error');

  if (searchParams.get('invited')) {
    return (
      <Alert severity="success" sx={{ width: '100%', mb: 2, borderRadius: 1, fontSize: '0.875rem' }}>
        Your account is ready. Sign in with your new password.
      </Alert>
    );
  }

  if (!errorParam) return null;

  let message = '';
  if (errorParam === 'invalid_invitation') {
    message = 'Invalid or expired invitation link';
  } else if (errorParam === 'missing_token') {
    message = 'Missing invitation token';
  }

  return (
    <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 1 }}>
      {message}
    </Alert>
  );
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      clearAllCookies();
      await post('/api/auth/login', { email: email.trim().toLowerCase(), password }, undefined, { redirectOnUnauthorized: false });

      const returnUrl = searchParams.get('returnUrl') || '/';
      router.push(returnUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  return (
    <LoginLayout>
      <Stack spacing={0.5} sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
          Sign in
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Use your TLC credentials to continue.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 1, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      <Suspense fallback={null}>
        <SearchParamAlerts />
      </Suspense>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus={!email}
            size="medium"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={authInputSx}
          />

          <TextField
            required
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            autoFocus={!!email}
            size="medium"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword((s) => !s)} edge="end" size="small" tabIndex={-1} sx={{ color: 'text.secondary' }}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={authInputSx}
          />

          <AuthSubmitButton loading={loading}>Sign in</AuthSubmitButton>
        </Stack>
      </Box>
    </LoginLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div></div>}>
      <LoginForm />
    </Suspense>
  );
}
